import React, { useMemo, useState } from "react";

import { getApiUrl } from "../../config/api";
import useRatePolling from "./hooks/useRatePolling";
import { Styles } from "./style/currencyCalculatorStyle";

const CURRENCY_RATE_URL = getApiUrl("currency-rate");
const CURRENCY_ERROR_MESSAGE = "دریافت نرخ ارزها با خطا مواجه شد.";
const RAW_FEE_PERCENT = Number(process.env.REACT_APP_CURRENCY_CONVERSION_FEE_PERCENT);
const FEE_PERCENT = Number.isFinite(RAW_FEE_PERCENT) && RAW_FEE_PERCENT >= 0
  ? RAW_FEE_PERCENT
  : 0;

const formatNumber = (value, maximumFractionDigits = 2) => Number(value).toLocaleString("fa-IR", {
  maximumFractionDigits,
});

export const calculateConversion = (amount, rate, direction, feePercent) => {
  const numericAmount = Number(amount);
  const numericRate = Number(rate);

  if (!Number.isFinite(numericAmount) || numericAmount <= 0 || !Number.isFinite(numericRate) || numericRate <= 0) {
    return { grossAmount: 0, feeAmount: 0, finalAmount: 0 };
  }

  const grossAmount = direction === "currency-to-toman"
    ? numericAmount * numericRate
    : numericAmount / numericRate;
  const feeAmount = grossAmount * (feePercent / 100);

  return {
    grossAmount,
    feeAmount,
    finalAmount: Math.max(grossAmount - feeAmount, 0),
  };
};

const CurrencyCalculator = () => {
  const { data: currencies, isLoading, error } = useRatePolling(
    CURRENCY_RATE_URL,
    CURRENCY_ERROR_MESSAGE
  );
  const [direction, setDirection] = useState("toman-to-currency");
  const [selectedCode, setSelectedCode] = useState("");
  const [amount, setAmount] = useState("");

  const availableCurrencies = useMemo(() => currencies.filter((currency) => (
    currency.code && Number(currency.price) > 0
  )), [currencies]);
  const activeCode = availableCurrencies.some((currency) => currency.code === selectedCode)
    ? selectedCode
    : availableCurrencies[0]?.code || "";
  const selectedCurrency = availableCurrencies.find((currency) => currency.code === activeCode);
  const conversion = calculateConversion(amount, selectedCurrency?.price, direction, FEE_PERCENT);
  const sourceUnit = direction === "toman-to-currency" ? "تومان" : activeCode;
  const resultUnit = direction === "toman-to-currency" ? activeCode : "تومان";

  const toggleDirection = () => {
    setDirection((currentDirection) => currentDirection === "toman-to-currency"
      ? "currency-to-toman"
      : "toman-to-currency");
  };

  return (
    <Styles>
      <section className="currency-calculator" aria-labelledby="currency-calculator-title">
        <div className="calculator-heading">
          <span className="eyebrow">محاسبه آنلاین</span>
          <h2 id="currency-calculator-title">ماشین حساب ارز</h2>
          <p>تبدیل بر اساس آخرین نرخ ثبت‌شده در سامانه</p>
        </div>

        {isLoading ? (
          <div className="calculator-message">در حال دریافت نرخ ارزها...</div>
        ) : availableCurrencies.length === 0 ? (
          <div className="calculator-message error">{error || "نرخ ارزی برای محاسبه وجود ندارد."}</div>
        ) : (
          <div className="calculator-form">
            {error && <div className="refresh-error">به‌روزرسانی نرخ‌ها انجام نشد؛ آخرین نرخ دریافتی استفاده می‌شود.</div>}

            <label htmlFor="calculator-currency">ارز موردنظر</label>
            <select
              id="calculator-currency"
              value={activeCode}
              onChange={(event) => setSelectedCode(event.target.value)}
            >
              {availableCurrencies.map((currency) => (
                <option key={currency.code} value={currency.code}>
                  {currency.title} ({currency.code})
                </option>
              ))}
            </select>

            <div className="direction-row">
              <span>{sourceUnit}</span>
              <button type="button" onClick={toggleDirection} aria-label="تغییر جهت تبدیل">⇄</button>
              <span>{resultUnit}</span>
            </div>

            <label htmlFor="calculator-amount">مبلغ به {sourceUnit}</label>
            <div className="amount-field">
              <input
                id="calculator-amount"
                type="number"
                min="0"
                step="any"
                inputMode="decimal"
                value={amount}
                onChange={(event) => setAmount(event.target.value)}
                placeholder="مبلغ را وارد کنید"
              />
              <span>{sourceUnit}</span>
            </div>

            <div className="rate-row">
              <span>نرخ هر {selectedCurrency?.title}</span>
              <strong>{formatNumber(selectedCurrency?.price)} تومان</strong>
            </div>
            <div className="rate-row">
              <span>کارمزد ({formatNumber(FEE_PERCENT)}٪)</span>
              <strong>{formatNumber(conversion.feeAmount, resultUnit === "تومان" ? 0 : 4)} {resultUnit}</strong>
            </div>

            <div className="calculator-result" aria-live="polite">
              <span>مبلغ نهایی</span>
              <strong>{formatNumber(conversion.finalAmount, resultUnit === "تومان" ? 0 : 4)} {resultUnit}</strong>
            </div>
          </div>
        )}
      </section>
    </Styles>
  );
};

export default CurrencyCalculator;
